// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * SelectWithOther
 *
 * Een dropdown met een lijst veelvoorkomende keuzes plus een "Anders"-optie.
 * Bij "Anders" verschijnt een vrij tekstveld. De gekozen of getypte waarde wordt
 * in hetzelfde enkele veld opgeslagen, zodat:
 *  - bestaande vrije invoer behouden blijft (waarde niet in de lijst → toont
 *    automatisch als "Anders" met de oorspronkelijke tekst);
 *  - exports, PDF's en Documentwijzer zonder aanpassing de juiste waarde tonen.
 *
 * Geen datamodel- of schemawijziging nodig: één string in, één string uit.
 */
import { useState } from 'react'
import { Select } from '@/components/ui/Select'
import { Input } from '@/components/ui/Input'

const OTHER = '__other__'

interface SelectWithOtherProps {
  label?:            string
  options:           string[]
  value:             string
  onChange:          (value: string) => void
  placeholder?:      string
  /** Tekst van de "Anders"-optie in de dropdown. */
  otherOptionLabel?: string
  /** Label van het vrije tekstveld dat bij "Anders" verschijnt. */
  otherFieldLabel?:  string
  otherPlaceholder?: string
  hint?:             string
}

export function SelectWithOther({
  label,
  options,
  value,
  onChange,
  placeholder = 'Maak een keuze...',
  otherOptionLabel = 'Anders',
  otherFieldLabel = 'Anders, namelijk',
  otherPlaceholder,
  hint,
}: SelectWithOtherProps) {
  const inList = options.includes(value)
  // forcedOther dekt het geval "Anders gekozen, maar nog niets getypt".
  const [forcedOther, setForcedOther] = useState(false)
  const showOther = !inList && (forcedOther || value.trim() !== '')

  const selectValue = inList ? value : (showOther ? OTHER : '')

  const handleSelect = (v: string) => {
    if (v === OTHER) {
      setForcedOther(true)
      if (inList) onChange('') // van bekende keuze naar vrij veld: leegmaken om te typen
    } else {
      setForcedOther(false)
      onChange(v)
    }
  }

  const opts = [
    ...options.map(o => ({ value: o, label: o })),
    { value: OTHER, label: otherOptionLabel },
  ]

  return (
    <div className="flex flex-col gap-2">
      <Select
        label={label}
        options={opts}
        value={selectValue}
        onChange={e => handleSelect(e.target.value)}
        placeholder={placeholder}
        hint={!showOther ? hint : undefined}
      />
      {showOther && (
        <Input
          label={otherFieldLabel}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={otherPlaceholder}
        />
      )}
    </div>
  )
}
